import React, { useContext, useEffect, useMemo } from 'react';
import { withRouter } from 'react-router';
import { useHistory } from 'react-router-dom';
import { axiosKK } from '../../services/networkRequest';
import {
  AdminOrdersResponse,
  AdminOxfordPackageOrdersResponse,
  Order,
  OrderListResponse,
  RemovalOrdersResponse,
  RemovalQuoteRequestsResponse,
} from '../../models/APIModels';
import Moment from 'moment';
import UserContext from '../../contexts/UserContext';
import { Role } from '../../utilities/Role';
import { useApi } from '../../services/useApi';
import {
  AdminPageShell,
  DataTable,
  DataTableColumn,
  ExportCsvButton,
  SectionHead,
} from '../../components/admin/Shared';
import { oxfordPaymentStatusLabel } from '../../utilities/OxfordPackageStatus';
import HubspotCancellationImport from '../../components/admin/HubspotCancellationImport';

type DashboardOrder = Order & {
  itemType?: 'quote' | 'oxford_package';
  tierName?: string;
  status?: string;
  customerEmail?: string;
  packageName?: string;
  paymentStatus?: string;
};

interface KpiTile {
  label: string;
  value: number;
  caption: string;
}

function AdminDashboardComponent() {
  const routerHistory = useHistory();
  const { user: loggedInUser } = useContext(UserContext);

  const { data: orders = [], loading, error } = useApi<DashboardOrder[]>(
    () =>
      Promise.all([
        axiosKK
          .get<OrderListResponse>('/orders')
          .catch(() => ({ data: { orders: [] } })),
        axiosKK
          .get<RemovalOrdersResponse>('/admin/removals/orders')
          .catch(() => ({ data: { orders: [] } })),
        axiosKK
          .get<RemovalQuoteRequestsResponse>('/admin/removals/quotes')
          .catch(() => ({ data: { quotes: [] } })),
        // Admin-only on the API; a warehouse user would only be refused.
        loggedInUser?.role === Role.Admin
          ? axiosKK
              .get<AdminOxfordPackageOrdersResponse>('/admin/oxford-packages/orders')
              .catch(() => ({ data: { orders: [] } }))
          : Promise.resolve({ data: { orders: [] } }),
      ]).then(([regularOrdersResult, removalOrdersResult, quoteRequestsResult, oxfordOrdersResult]) => {
        const quotesWithType = (quoteRequestsResult.data.quotes || []).map((q) => ({
          ...q,
          itemType: 'quote' as const,
        }));
        const oxfordWithType = (oxfordOrdersResult.data.orders || []).map((o) => ({
          ...o,
          itemType: 'oxford_package' as const,
        }));
        const allOrders = [
          ...(regularOrdersResult.data.orders || []),
          ...(removalOrdersResult.data.orders || []),
          ...quotesWithType,
          ...oxfordWithType,
        ] as DashboardOrder[];
        return allOrders.sort((a, b) => b.createdAt - a.createdAt);
      }),
    [],
    'admin-dashboard-orders'
  );

  // Today's jobs come from the logistics list, which covers every order. The
  // records below are only the last 30 days' orders, so counting today's jobs
  // from them missed every older order with a collection or return today.
  const isAdmin = loggedInUser?.role === Role.Admin;
  const { data: todaysJobs } = useApi<{ collections: number; returns: number } | null>(
    () => {
      if (!isAdmin) return Promise.resolve(null);
      const day = Moment().format('YYYY-MM-DD');
      const count = (type: 'collection' | 'return') =>
        axiosKK
          .get<AdminOrdersResponse>('/logistics/admin/orders', {
            params: { fromDate: day, toDate: day, type, pageSize: 1 },
          })
          .then((r) => r.data.totalCount);
      return Promise.all([count('collection'), count('return')])
        .then(([collections, returns]) => ({ collections, returns }))
        .catch(() => null);
    },
    [isAdmin],
    'admin-dashboard-todays-jobs'
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const kpis: KpiTile[] = useMemo(() => {
    const today = Moment().startOf('day');
    const isToday = (ms?: number) =>
      typeof ms === 'number' && Moment(ms).isSame(today, 'day');
    const isLiveOrder = (o: DashboardOrder) =>
      !o.itemType && o.orderType !== 'removal' && !o.cancelledAt;

    const pendingQuotes = orders.filter((o) => o.itemType === 'quote').length;
    const newOrders = orders.filter(isLiveOrder).length;
    // A warehouse user cannot read the logistics list, so falls back to the
    // records loaded here.
    const todaysCollections =
      todaysJobs?.collections ??
      orders.filter((o) => isLiveOrder(o) && isToday(o.collectionDate as unknown as number)).length;
    const todaysReturns =
      todaysJobs?.returns ??
      orders.filter((o) => isLiveOrder(o) && isToday(o.deliveryDate as unknown as number)).length;

    return [
      {
        label: 'New orders',
        value: newOrders,
        caption: 'Booked in the last 30 days',
      },
      {
        label: 'Pending quotes',
        value: pendingQuotes,
        caption: 'Awaiting review',
      },
      {
        label: 'Today · collections',
        value: todaysCollections,
        caption: Moment().format('ddd D MMM'),
      },
      {
        label: 'Today · returns',
        value: todaysReturns,
        caption: Moment().format('ddd D MMM'),
      },
    ];
  }, [orders, todaysJobs]);

  const navigateToOrder = (order: DashboardOrder) => {
    if (order.itemType === 'quote') {
      routerHistory.push(`/admin/removals/quote/${order.id}`, { quote: order });
    } else if (order.itemType === 'oxford_package') {
      routerHistory.push(`/admin/oxford-packages/${order.id}`, { order });
    } else {
      routerHistory.push(`/admin/order/${order.id}`, { order });
    }
  };

  const customerNameOf = (order: DashboardOrder): string => {
    if (order.itemType === 'quote') {
      return order.customer
        ? `${order.customer.firstName || ''} ${order.customer.lastName || ''}`.trim()
        : order.customerEmail || 'Unknown';
    }
    if (order.customer) {
      return `${order.customer.firstName || ''} ${order.customer.lastName || ''}`.trim();
    }
    return 'Unknown';
  };

  const orderTypeOf = (order: DashboardOrder): string => {
    if (order.itemType === 'quote') return `Quote — ${order.tierName || 'Unknown'}`;
    if (order.itemType === 'oxford_package') return `Oxford package — ${order.packageName || 'Unknown'}`;
    if (order.orderType === 'removal') return 'Removal';
    if (order.suitcaseOrder) return 'Suitcase';
    if (order.storageRequired) return 'Storage';
    return 'Shipping';
  };

  const collectionDateOf = (order: DashboardOrder): string => {
    if (order.itemType === 'quote') return 'Pending Quote';
    if (order.itemType === 'oxford_package') return '—';
    if (order.orderType === 'removal') {
      return order.moveDate ? Moment(order.moveDate).format('ddd D MMM YYYY') : 'Not Set';
    }
    return order.collectionDate
      ? Moment(order.collectionDate).format('ddd D MMM YYYY')
      : 'Not Set';
  };

  const deliveryDateOf = (order: DashboardOrder): string => {
    if (order.itemType === 'quote') return (order.status || '').toUpperCase();
    if (order.itemType === 'oxford_package') return '—';
    return order.deliveryDate
      ? Moment(order.deliveryDate).format('ddd D MMM YYYY')
      : 'Not Set';
  };

  const partnersBadge = (order: DashboardOrder): React.ReactNode => {
    if (order.itemType === 'quote' || order.orderType === 'removal') return '';
    if (order.itemType === 'oxford_package') return '';
    if (!order.storageRequired) return '';

    const hasCollection = order.collectionDriverId ?? order.assignedDriverId;
    const hasDelivery = order.deliveryDriverId ?? order.assignedDriverId;
    const hasWarehouse = order.assignedWarehouseId;
    const bothCouriers = hasCollection && hasDelivery;

    if (bothCouriers && hasWarehouse) {
      return <span className="assignment-badge assignment-full">All Assigned</span>;
    }
    if (bothCouriers && !hasWarehouse) {
      return <span className="assignment-badge assignment-couriers">Both Couriers</span>;
    }
    if (hasCollection && hasWarehouse) {
      return <span className="assignment-badge assignment-partial">Collection + Warehouse</span>;
    }
    if (hasDelivery && hasWarehouse) {
      return <span className="assignment-badge assignment-partial">Return + Warehouse</span>;
    }
    if (hasCollection) {
      return <span className="assignment-badge assignment-minimal">Collection Courier</span>;
    }
    if (hasDelivery) {
      return <span className="assignment-badge assignment-minimal">Return Courier</span>;
    }
    if (hasWarehouse) {
      return <span className="assignment-badge assignment-warehouse">Warehouse Only</span>;
    }
    return <span className="assignment-badge assignment-none">Unassigned</span>;
  };

  // An order's latest status is its HubSpot deal stage (order placed, paid in
  // full, monthly payments started, last monthly bill paid, …). It used to be
  // guessed from flags, and "empty boxes sent" read as "In storage" — which is
  // how a cancelled order showed as stored.
  const latestStatusOf = (order: DashboardOrder) =>
    (order.statuses || []).reduce<DashboardOrder['statuses'][number] | undefined>(
      (latest, s) => (!latest || s.id > latest.id ? s : latest),
      undefined
    );

  // Status pill. Reads at a glance: amber = quote pending, purple = booked,
  // lime = paying for storage, slate = finished or cancelled.
  const statusPillOf = (order: DashboardOrder): React.ReactNode => {
    let tone: 'amber' | 'purple' | 'lime' | 'slate' = 'slate';
    let label = '—';
    if (order.itemType === 'quote') {
      tone = 'amber';
      label = (order.status || 'Pending').toUpperCase();
    } else if (order.itemType === 'oxford_package') {
      // Paid reads lime; anything still waiting on Stripe reads amber.
      label = oxfordPaymentStatusLabel(order.paymentStatus);
      if (order.paymentStatus === 'succeeded') tone = 'lime';
      else if (order.paymentStatus === 'failed' || order.paymentStatus === 'canceled') tone = 'slate';
      else tone = 'amber';
    } else if (order.cancelledAt) {
      tone = 'slate';
      label = 'Cancelled';
    } else if (order.orderComplete) {
      tone = 'slate';
      label = 'Complete';
    } else if (order.orderType !== 'removal' && latestStatusOf(order)?.status?.type) {
      const latest = latestStatusOf(order)!;
      label = latest.status.type;
      tone = latest.statusId === 8 ? 'slate' : latest.statusId === 7 ? 'lime' : 'purple';
    } else if (order.collectionDate) {
      tone = 'purple';
      label = 'Booked';
    } else {
      tone = 'slate';
      label = 'Draft';
    }
    return (
      <span
        className={`admin-status-pill admin-status-pill--${tone}`}
        title={label}
      >
        <span className="admin-status-pill__dot" aria-hidden="true" />
        {label}
      </span>
    );
  };

  const postcodeOf = (order: DashboardOrder): string => {
    const p = order.collectionPostcode;
    return p ? String(p).toUpperCase() : '—';
  };

  const boxesOf = (order: DashboardOrder): React.ReactNode => {
    if (order.itemType === 'quote') return '—';
    const count = order.orderBoxes?.length ?? 0;
    return count || '—';
  };

  // Columns ordered by ops scan-priority: identity → context → action dates
  // → location → volume → assignment → status → system meta. Font-weight via
  // `emphasis` makes the row identity (Customer) read first; meta recedes.
  const columns: DataTableColumn<DashboardOrder>[] = [
    {
      key: 'customerName',
      label: 'Customer',
      emphasis: 'primary',
      render: (o) => customerNameOf(o),
    },
    {
      key: 'orderType',
      label: 'Type',
      emphasis: 'secondary',
      render: (o) => orderTypeOf(o),
    },
    {
      key: 'collectionDate',
      label: 'Collection',
      emphasis: 'secondary',
      render: (o) => collectionDateOf(o),
    },
    {
      key: 'deliveryDate',
      label: 'Delivery',
      emphasis: 'secondary',
      render: (o) => deliveryDateOf(o),
    },
    {
      key: 'collectionPostcode',
      label: 'Postcode',
      emphasis: 'secondary',
      render: (o) => postcodeOf(o),
    },
    {
      key: 'boxes',
      label: 'Boxes',
      align: 'numeric',
      emphasis: 'secondary',
      render: (o) => boxesOf(o),
    },
    {
      key: 'partners',
      label: 'Partners',
      render: (o) => partnersBadge(o),
    },
    {
      key: 'status',
      label: 'Status',
      render: (o) => statusPillOf(o),
    },
    {
      key: 'createdAt',
      label: 'Created',
      emphasis: 'meta',
      render: (o) =>
        o.createdAt ? Moment(o.createdAt).format('D MMM YYYY') : '—',
    },
    {
      key: 'id',
      label: 'ID',
      emphasis: 'meta',
      align: 'numeric',
      render: (o) => o.id,
    },
  ];

  const titleByRole =
    loggedInUser?.role === Role.Warehouse
      ? 'Warehouse portal'
      : 'Admin portal';

  return (
    <AdminPageShell title={titleByRole}>
      <section
        className="admin-dashboard-kpis"
        aria-label="Today at a glance"
      >
        {kpis.map((kpi) => (
          <article key={kpi.label} className="admin-dashboard-kpi">
            <span className="admin-dashboard-kpi__label">{kpi.label}</span>
            <span
              className="admin-dashboard-kpi__value"
              aria-live="polite"
            >
              {loading ? '—' : kpi.value}
            </span>
            <span className="admin-dashboard-kpi__caption">{kpi.caption}</span>
          </article>
        ))}
      </section>

      <section
        className="admin-dashboard-orders"
        aria-label="Recent orders"
      >
        <SectionHead
          title="Recent records"
          caption={`The latest ${orders.length} orders, removals, quotes and Oxford packages — click any row to open the record.`}
          infoKey="dashboard.recent-records"
          actions={
            <ExportCsvButton endpoint="/orders/CSV" filename="OrderBoxes.csv">
              Export orders
            </ExportCsvButton>
          }
        />

        <DataTable<DashboardOrder>
          columns={columns}
          rows={orders}
          keyOf={(o) => `${o.itemType || o.orderType || 'order'}-${o.id}`}
          loading={loading}
          loadingMessage="Loading orders…"
          emptyMessage={
            error
              ? 'Failed to load orders. Please refresh the page.'
              : 'No orders found.'
          }
          onRowClick={navigateToOrder}
        />
      </section>

      {isAdmin && <HubspotCancellationImport />}
    </AdminPageShell>
  );
}

export default withRouter(AdminDashboardComponent as any);
